import * as React from 'react';

import { enqueueSnackbar } from 'notistack';

import { SSEProgressEvent, useSSEMutation } from '@itp/talent/hooks/useFetchSSE';
import { validateParsedData } from '@itp/talent/pages/talentProfilePage/preprocessParsedData';
import { UpdateProfileDialog } from '@itp/talent/pages/talentProfilePage/widgets/updateCVdialog';

const PARSE_STAGE_LABELS: Record<string, string> = {
  downloading: 'Downloading CV...',
  extracting_text: 'Extracting text from PDF...',
  parsing: 'Analyzing CV with AI...',
};

type ConfirmCallback = (data: Record<string, unknown>) => Promise<void>;

interface CVParseContextValues {
  clearPendingParse: () => void;
  isParseActive: boolean;
  startCVParse: (cvId: string, onConfirm: ConfirmCallback) => void;
}

const CVParseContext = React.createContext<CVParseContextValues>({
  clearPendingParse: () => {},
  isParseActive: false,
  startCVParse: () => {},
});

export const useCVParse = () => React.useContext(CVParseContext);

export const CVParseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isParseLoading, setIsParseLoading] = React.useState(false);
  const [parseStage, setParseStage] = React.useState<null | string>(null);
  const [parsedData, setParsedData] = React.useState<null | Record<string, unknown>>(null);
  const [partialData, setPartialData] = React.useState<Record<string, unknown>>({});

  const confirmCallbackRef = React.useRef<ConfirmCallback | null>(null);
  const isParseLoadingRef = React.useRef(false);
  isParseLoadingRef.current = isParseLoading;

  const { abort: abortParse, mutateAsync: parseCVAsync } = useSSEMutation<
    Record<string, unknown>,
    { cvId: string }
  >(
    { url: '/upload/parse-cv', urlPrefix: '/api' },
    {
      messageFail: null,
      onField: (key: string, value: unknown) => {
        setPartialData((prev) => ({ ...prev, [key]: value }));
      },
      onFieldReset: () => {
        setPartialData({});
      },
      onProgress: (event: SSEProgressEvent) => {
        if (event.stage) {
          setParseStage(PARSE_STAGE_LABELS[event.stage] || event.stage);
        }
      },
    },
  );

  React.useEffect(() => {
    return () => {
      abortParse();
    };
  }, [abortParse]);

  const clearPendingParse = React.useCallback(() => {
    confirmCallbackRef.current = null;
    if (isParseLoadingRef.current) {
      abortParse();
    }
    setIsDialogOpen(false);
    setParsedData(null);
    setPartialData({});
    setIsParseLoading(false);
    setParseStage(null);
  }, [abortParse]);

  const startCVParse = React.useCallback(
    (cvId: string, onConfirm: ConfirmCallback) => {
      confirmCallbackRef.current = onConfirm;
      setIsParseLoading(true);
      setParseStage('Starting...');
      setParsedData(null);
      setPartialData({});
      setIsDialogOpen(true);

      parseCVAsync({ cvId })
        .then((result) => {
          if (result) {
            const validData = validateParsedData(result);
            setParsedData(validData);
          } else {
            setIsDialogOpen(false);
            enqueueSnackbar('CV parsing returned no data.', {
              autoHideDuration: 5000,
              variant: 'warning',
            });
          }
        })
        .catch((err: any) => {
          setIsDialogOpen(false);
          if (err?.name !== 'AbortError') {
            enqueueSnackbar(err?.message || 'Failed to parse CV. Please try again.', {
              autoHideDuration: 5000,
              variant: 'error',
            });
          }
        })
        .finally(() => {
          setIsParseLoading(false);
          setParseStage(null);
        });
    },
    [parseCVAsync],
  );

  const handleConfirm = React.useCallback(async (dataToSubmit: Record<string, unknown>) => {
    setIsDialogOpen(false);

    if (!confirmCallbackRef.current) {
      enqueueSnackbar('Please return to your profile page to apply changes.', {
        autoHideDuration: 5000,
        variant: 'warning',
      });
      setParsedData(null);
      setPartialData({});
      return;
    }

    try {
      await confirmCallbackRef.current(dataToSubmit);
    } catch (err) {
      enqueueSnackbar('Failed to update profile.', { autoHideDuration: 5000, variant: 'error' });
      console.error('CV parse confirm failed:', err);
    }
    setParsedData(null);
    setPartialData({});
    confirmCallbackRef.current = null;
  }, []);

  const handleClose = React.useCallback(() => {
    if (isParseLoadingRef.current) {
      abortParse();
    }
    setIsDialogOpen(false);
    setParsedData(null);
    setPartialData({});
    setIsParseLoading(false);
    setParseStage(null);
    confirmCallbackRef.current = null;
  }, [abortParse]);

  const dialogData = isParseLoading ? partialData : parsedData || {};
  const hasPartialFields = isParseLoading && Object.keys(partialData).length > 0;

  const value = React.useMemo(
    () => ({ clearPendingParse, isParseActive: isDialogOpen, startCVParse }),
    [clearPendingParse, isDialogOpen, startCVParse],
  );

  return (
    <CVParseContext.Provider value={value}>
      {children}
      <UpdateProfileDialog
        hasPartialFields={hasPartialFields}
        isLoading={isParseLoading}
        loadingStage={parseStage}
        onClose={handleClose}
        onConfirm={handleConfirm}
        open={isDialogOpen}
        updateData={dialogData}
      />
    </CVParseContext.Provider>
  );
};
